import React, { useCallback, useEffect, useId, useState } from 'react';
import ReactDOM from 'react-dom';
import { FocusTrap } from 'focus-trap-react';
import { useLayer } from 'react-laag';
import { Breakpoints, intl, useBreakpoint } from '../../lib';
import { DEFAULT_PLACEMENT_CONFIG } from '../../util';
import type { ClassNameProp } from '../common-types';
import { Direction } from '../common-types';
import { Badge } from './Badge';
import { Button } from './Button';
import { Drawer } from './Drawer';
import { Icon } from './Icon';
import { Tool } from './Tool';
import { Checkbox } from '../form';
import { VBox } from '../layout';

/**
 * A single filter item.
 */
export interface FilterItem {
  /** Unique identifier for the item */
  id: string;
  /** Display label for the item */
  label: string;
}

/**
 * Props for the {@link Filter} component.
 */
export interface FilterProps extends ClassNameProp {
  /**
   * Array of filter items to display as checkboxes. Must contain at least one item.
   */
  items: [FilterItem, ...FilterItem[]];
  /**
   * Currently selected filter values (controlled state).
   */
  selectedValues: string[];
  /**
   * Callback fired immediately when a filter is toggled.
   * @param newValues The updated array of selected filter IDs
   */
  onChange: (newValues: string[]) => void;
  /**
   * Title displayed in the filter header.
   */
  title: string;
  /**
   * Optional aria-label for the filter button.
   */
  ariaLabel?: string;
  /** Reports the mobile drawer lifecycle so its owner can isolate the background. */
  setOverlayOpen?: (isOpen: boolean) => void;
}

/**
 * Props for the internal {@link FilterList} component.
 */
interface FilterListProps {
  items: FilterItem[];
  selectedValues: string[];
  onChange: (newValues: string[]) => void;
}

/**
 * Shared filter list component that renders checkboxes for filter items.
 * This component is used by both the mobile Drawer and desktop Popover variants.
 *
 * @param props {@link FilterListProps} object
 * @return the created {@link React.JSX.Element}
 * @constructor
 */
const FilterList = ({
  items,
  selectedValues,
  onChange
}: Readonly<FilterListProps>): React.JSX.Element => {
  const handleToggle = useCallback(
    (itemId: string) => {
      const isSelected = selectedValues.includes(itemId);
      const newValues = isSelected
        ? selectedValues.filter(id => id !== itemId)
        : [...selectedValues, itemId];
      onChange(newValues);
    },
    [selectedValues, onChange]
  );

  return (
    <VBox className="ehs-filter-list">
      {items.map(item => (
        <Checkbox
          key={item.id}
          label={item.label}
          value={selectedValues.includes(item.id)}
          onChange={() => handleToggle(item.id)}
        />
      ))}
    </VBox>
  );
};

/**
 * A unified, responsive Filter component that displays a filter button with badge.
 * When clicked, it opens a list of filter items as checkboxes.
 *
 * On mobile: Uses a {@link Drawer} component with a "Close" button.
 * On desktop: Uses a popover (similar to {@link MenuTool}) positioned via react-laag.
 *
 * Filtering is applied immediately when checkboxes are toggled (no draft state or "Apply" button).
 *
 * @param props {@link FilterProps} object
 * @return the created {@link React.JSX.Element}
 * @constructor
 */
export function Filter({
  items,
  selectedValues,
  onChange,
  title,
  ariaLabel,
  setOverlayOpen,
  className = ''
}: Readonly<FilterProps>): React.JSX.Element {
  const isMobile = useBreakpoint(Breakpoints.MOBILE);
  const [isOpen, setIsOpen] = useState(false);
  const menuId = useId();
  useEffect(() => {
    if (!isMobile || !isOpen || !setOverlayOpen) {
      return;
    }
    setOverlayOpen(true);
    return () => setOverlayOpen(false);
  }, [isMobile, isOpen, setOverlayOpen]);

  const hasActiveFilters = selectedValues.length > 0;

  const handleToggleOpen = useCallback(() => {
    setIsOpen(prev => !prev);
  }, []);

  const handleClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  const { layerProps, triggerProps, renderLayer } = useLayer({
    isOpen: isOpen && !isMobile,
    onOutsideClick: handleClose,
    ...DEFAULT_PLACEMENT_CONFIG
  });

  const filterButton = (
    <div className={['ehs-filter-button-wrapper', className].join(' ')}>
      <Tool
        {...(isMobile ? {} : triggerProps)}
        className={isOpen ? 'ehs-filter-trigger active' : 'ehs-filter-trigger'}
        icon="shui_filter"
        onClick={handleToggleOpen}
        tooltip={ariaLabel ?? title}
        aria-expanded={isOpen}
        aria-controls={menuId}
        aria-haspopup="dialog"
        aria-label={ariaLabel ?? title}
      />
      {hasActiveFilters && (
        <Badge
          className="ehs-filter-badge"
          badgeType="info"
          label={selectedValues.length.toString()}
        />
      )}
    </div>
  );

  if (isMobile) {
    return (
      <>
        {filterButton}
        {isOpen &&
          ReactDOM.createPortal(
            <Drawer
              id={menuId}
              direction={Direction.BOTTOM}
              onOverlayClicked={handleClose}
              enableFocusTrap
              title={title}
              open={isOpen}
              roundedCorners={true}
              className="ehs-filter-drawer"
              headerToolLeft={
                <Icon
                  iconName="shui_filter"
                  className="drawer-header-icon"
                  aria-label={''}
                />
              }
              tool={
                <Tool
                  icon="delete"
                  tooltip={intl('view.button.close')}
                  onClick={handleClose}
                  className="drawer-close-button"
                />
              }
            >
              <div className="ehs-filter-drawer-content">
                <FilterList
                  items={items}
                  selectedValues={selectedValues}
                  onChange={onChange}
                />
              </div>
              <div className="ehs-filter-drawer-footer">
                <Button
                  variant="primary"
                  scale={'medium'}
                  text={intl('view.button.close')}
                  onClick={handleClose}
                />
              </div>
            </Drawer>,
            document.body
          )}
      </>
    );
  }

  return (
    <>
      {filterButton}
      {isOpen &&
        renderLayer(
          <FocusTrap
            focusTrapOptions={{
              allowOutsideClick: true,
              escapeDeactivates: () => {
                handleClose();
                return true;
              },
              preventScroll: true,
              initialFocus: false
            }}
          >
            <div
              id={menuId}
              className="ehs-filter-popover"
              {...layerProps}
              style={{ ...layerProps.style }}
              ref={layerProps.ref}
              role="dialog"
              aria-modal="true"
              aria-hidden={!isOpen}
              aria-label={title}
              tabIndex={-1}
            >
              <div className="ehs-filter-popover-header">
                <h3>{title}</h3>
              </div>
              <FilterList
                items={items}
                selectedValues={selectedValues}
                onChange={onChange}
              />
            </div>
          </FocusTrap>
        )}
    </>
  );
}
